import {
  ApiError,
  beginRedirectLogin,
  completeRedirectLogin,
  isRedirectLoginResponse,
  RedirectLoginError,
} from '@nyce/admin-kit/api';
import type { LoginOptions, OAuthRequest } from './api';
import { api, storeLogin } from './session';
import { withoutAutoSso } from './environment';

/**
 * Federated sign-in (Entra ID through nyce-logic-auth, `docs/saml-entra.md`): to the hub itself (operator tenant at the
 * hub's AUTH) or to one AUTH environment. The browser goes to that AUTH's `/authorize` and comes back to
 * `/login/callback`; the hub redeems the code server-side (`/login/code`, `/auth-targets/{id}/code`).
 *
 * After the hub's own federated sign-in the console signs in to every other environment with federated sign-in, one
 * round trip each (`auto` = the environments this chain has tried, so each is tried once): Entra's session makes them
 * silent. One that refuses keeps its reason on its sign-in card; the chain goes on and ends on the page the user left.
 * Only environments whose federated sign-in came back in this browser before take part: AUTH shows a misconfigured
 * login app (this hub's callback not listed) as its own error page, which would strand the user there. The mark is
 * dropped before leaving and set again on the way back, so a round trip that never returns is not repeated.
 */
export const CALLBACK_PATH = '/login/callback';

type Context =
  | { kind: 'hub'; next: string }
  | { kind: 'target'; target: string; next: string; auto?: string[] }
  | { kind: 'mcp'; request: OAuthRequest; next: string };

/** The MCP consent page (docs/mcp.md): an MCP client's authorization request lands here. */
export const OAUTH_AUTHORIZE_PATH = '/oauth/authorize';

/** Leaving for AUTH (replaced in tests). */
export const browser = { go: (url: string) => window.location.assign(url) };

const callbackUri = () => `${window.location.origin}${CALLBACK_PATH}`;

const WORKED = 'oph:sso-ok:';
const worked = {
  has: (id: string) => {
    try {
      return localStorage.getItem(WORKED + id) === '1';
    } catch {
      return false;
    }
  },
  set: (id: string, ok: boolean) => {
    try {
      if (ok) localStorage.setItem(WORKED + id, '1');
      else localStorage.removeItem(WORKED + id);
    } catch {
      /* private mode: no automatic sign-in */
    }
  },
};

/**
 * The next environment of an automatic sign-in chain: federated sign-in configured, not the hub's own session, not
 * signed in, not tried yet. Returns its `/authorize` URL (the pending sign-in is stored), or null when the chain is done.
 */
async function nextAutoSignIn(tried: string[], next: string): Promise<string | null> {
  const { loadTargets, targetSession } = await import('./identity/session');
  const t = (await loadTargets()).find(
    (x) =>
      x.sso &&
      !x.sharesHubSession &&
      !tried.includes(x.id) &&
      worked.has(x.id) &&
      !targetSession(x.id).isSignedIn()
  );
  if (!t?.sso) return null;
  worked.set(t.id, false);
  let url: string | null = null;
  await beginRedirectLogin({
    authUrl: t.authUrl,
    ...t.sso,
    redirectUri: callbackUri(),
    context: { kind: 'target', target: t.id, next, auto: [...tried, t.id] } satisfies Context,
    navigate: (u) => {
      url = u;
    },
  });
  return url;
}
// without another hub's `sso=auto`: the page to come back to must not start a sign-in again
const here = () => withoutAutoSso(window.location.pathname, window.location.search);

export function startHubSso(sso: NonNullable<LoginOptions['sso']>): Promise<void> {
  const next = window.location.pathname === CALLBACK_PATH ? '/' : here();
  return beginRedirectLogin({
    ...sso,
    redirectUri: callbackUri(),
    context: { kind: 'hub', next } satisfies Context,
    navigate: (u) => browser.go(u),
  });
}

export function startTargetSso(target: {
  id: string;
  authUrl: string;
  sso?: { clientId: string; tenant: string } | null;
}): Promise<void> {
  if (!target.sso) return Promise.reject(new Error('No federated sign-in for this environment.'));
  return beginRedirectLogin({
    authUrl: target.authUrl,
    ...target.sso,
    redirectUri: callbackUri(),
    context: { kind: 'target', target: target.id, next: here() } satisfies Context,
    navigate: (u) => browser.go(u),
  });
}

/**
 * Allow on the MCP consent page: an Entra sign-in of its own (the console's session is not touched), redeemed by the hub
 * for the MCP client instead of becoming a console session.
 */
export function startMcpSso(sso: NonNullable<LoginOptions['sso']>, request: OAuthRequest): Promise<void> {
  return beginRedirectLogin({
    ...sso,
    redirectUri: callbackUri(),
    context: { kind: 'mcp', request, next: here() } satisfies Context,
    navigate: (u) => browser.go(u),
  });
}

export const isSsoCallback = () =>
  window.location.pathname === CALLBACK_PATH && isRedirectLoginResponse(window.location.search);

const MESSAGES: Record<string, string> = {
  access_denied: 'The sign-in was refused',
  scope_required: 'Your account has no Operations Hub access (needs an ophub:* scope).',
  invalid_credentials: 'The sign-in could not be completed. Please try again.',
};

/**
 * Finishes the sign-in this tab started. Returns where to go next and, when it failed, why (and whether the hub
 * session is the one that failed — then the sign-in page is next).
 */
export function finishSso(): Promise<{ next: string; error?: string; hub: boolean; external?: boolean }> {
  // Once per page load: React StrictMode runs effects twice, and the state is single-use.
  finishing ??= finish();
  return finishing;
}
let finishing: Promise<{ next: string; error?: string; hub: boolean; external?: boolean }> | null = null;

async function finish(): Promise<{ next: string; error?: string; hub: boolean; external?: boolean }> {
  let context: Context | undefined;
  try {
    const result = completeRedirectLogin(window.location.search);
    context = result.context as Context;
    const body = { code: result.code, codeVerifier: result.codeVerifier, redirectUri: result.redirectUri };
    if (context?.kind === 'mcp') {
      // back to the MCP client with its code: the browser leaves the console
      const { redirect } = await api.oauthApprove({ ...context.request, login: body });
      return { next: redirect, hub: false, external: true };
    }
    if (context?.kind === 'target') {
      const { signInTargetWithCode } = await import('./identity/session');
      await signInTargetWithCode(context.target, body);
      worked.set(context.target, true);
    } else {
      storeLogin(await api.loginCode(body));
    }
    const next = context?.next || '/';
    const tried = context?.kind === 'target' ? context.auto : [];
    const onward = tried ? await nextAutoSignIn(tried, next).catch(() => null) : null;
    if (onward) return { next: onward, hub: false, external: true };
    return { next, hub: context?.kind !== 'target' };
  } catch (e) {
    if (e instanceof RedirectLoginError) context ??= e.context as Context | undefined;
    const code = e instanceof RedirectLoginError || e instanceof ApiError ? e.code : undefined;
    const message = e instanceof Error ? e.message : 'Sign-in failed';
    const error =
      code === 'access_denied'
        ? `${MESSAGES.access_denied}: ${message}`
        : (code && MESSAGES[code]) || message;
    if (context?.kind === 'target') {
      const { noteTargetLoginError } = await import('./identity/session');
      noteTargetLoginError(context.target, error);
      if (context.auto) {
        const onward = await nextAutoSignIn(context.auto, context.next || '/').catch(() => null);
        if (onward) return { next: onward, hub: false, external: true };
      }
    }
    if (context?.kind === 'mcp') {
      // the consent page again, with why
      const back = new URL(context.next, window.location.origin);
      back.searchParams.set('login_error', error);
      return { next: `${back.pathname}${back.search}`, hub: false };
    }
    return { next: context?.next || '/', error, hub: context?.kind !== 'target' };
  }
}
